import { ref, computed } from 'vue'
import { StreamLogType } from '@/streams/types'
import type { StreamKey } from '@/streams/types'

export type DebugDiagnosticSource =
  | 'rfq'
  | 'cache'
  | 'stream'
  | 'wallet'
  | 'lifecycle'
  | 'scheduler'
  | 'telemetry'
  | 'performance'

export type DebugDiagnosticEvent = {
  id: string
  message: string
  timestamp: number
  level: StreamLogType
  streamKey?: StreamKey
  source: DebugDiagnosticSource
  attributes?: Record<string, unknown>
}

const MAX_DEBUG_DIAGNOSTIC_EVENTS = 200

const debugDiagnosticEvents = ref<DebugDiagnosticEvent[]>([])
const isDebugDiagnosticsEnabled = ref(false)

export function useDebugDiagnostics() {
  return {
    clear: clearDebugDiagnosticEvents,
    clearWhere: clearDebugDiagnosticEventsWhere,
    record: recordDebugDiagnosticEvent,
    setEnabled: setDebugDiagnosticsEnabled,
    events: computed(() => debugDiagnosticEvents.value),
    isEnabled: computed(() => isDebugDiagnosticsEnabled.value)
  }
}

export function setDebugDiagnosticsEnabled(isEnabled: boolean) {
  isDebugDiagnosticsEnabled.value = isEnabled
}

export function clearDebugDiagnosticEvents() {
  debugDiagnosticEvents.value = []
}

export function clearDebugDiagnosticEventsWhere(
  predicate: (event: DebugDiagnosticEvent) => boolean
) {
  debugDiagnosticEvents.value = debugDiagnosticEvents.value.filter(
    (event) => !predicate(event)
  )
}

export function recordDebugDiagnosticEvent(
  event: Omit<DebugDiagnosticEvent, 'id' | 'level' | 'timestamp'> & {
    timestamp?: number
    level?: StreamLogType
  }
) {
  if (!isDebugDiagnosticsEnabled.value) {
    return
  }

  debugDiagnosticEvents.value = [
    {
      ...event,
      id: createDebugDiagnosticEventId(),
      timestamp: event.timestamp ?? Date.now(),
      level: event.level ?? StreamLogType.Log
    },
    ...debugDiagnosticEvents.value
  ].slice(0, MAX_DEBUG_DIAGNOSTIC_EVENTS)
}

export function resetDebugDiagnosticsForTests() {
  clearDebugDiagnosticEvents()
  setDebugDiagnosticsEnabled(false)
}

function createDebugDiagnosticEventId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}
